import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid2,
  MenuItem,
  Select,
  SelectChangeEvent,
  TablePagination,
} from "@mui/material";
import moment from "moment";
import {
  FetchSites,
  GetAllRecentInsights,
  GetRecentInsights,
} from "../../ApiServices/ApiRequests";
import {
  RecentInsights,
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import SearchBar from "@mkyy/mui-search-bar";
import { t } from "i18next";
import {
  DropDownOptions,
  MapResponseToDropdown,
  SitesInfo,
} from "../../Models/Models";
import { mapResponseToDropdown } from "../../Utils/CommonFunctions";
import dayjs from "dayjs";
import { JSX } from "react/jsx-runtime";
import {
  MasterDataContextType,
  useMasterData,
} from "../../ContextAPI/MasterContext";
import Loading from "../../Controller/LoadingController/Loading";

// Define types
interface InsightCardProps {
  groupName: string;
  deviceName: string;
  message: string;
  dateTime: string;
}

const InsightsScreen: React.FC = () => {
  const { masterState, dispatchMaster } = useMasterData();

  const [insights, setInsights] = useState<InsightCardProps[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("today");
  const [fromDate, setFromDate] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [selectedSiteCode, setSelectedSiteCode] = useState<string>("");
  const [siteOptions, setSiteOptions] = useState<DropDownOptions[]>([]);
  const handleInputChange = useCallback(
    (
      field: keyof MasterDataContextType,
      value:
        | string
        | boolean
        | DropDownOptions[]
        | number
        | dayjs.Dayjs
        | null
        | { lat: number; lng: number }
    ) => {
      dispatchMaster({ type: "SET_FIELD", field, value });
    },
    [dispatchMaster]
  );

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("LoginDetails") || "{}");

    fetchSitesData(user?.organization[0]?.orgCode);
  }, []);

  const fetchSitesData = async (orgCode: string): Promise<void> => {
    try {
      setLoading(true);
      const data: SitesInfo[] = await FetchSites(orgCode);
      const sitesOptions: MapResponseToDropdown[] = mapResponseToDropdown(
        data,
        "siteName",
        "siteCode",
        "latitude",
        "longitude"
      );
      setSiteOptions(sitesOptions);

      if (sitesOptions.length > 0) {
        setSelectedSiteCode(sitesOptions[0]?.value);
      }
    } catch (error) {
      console.error("Error loading site Options data", error);
    } finally {
      setLoading(false);
    }
  };
  console.log("selectedSiteCode", selectedSiteCode);

  // Fetch data from API
  const fetchRecentInsights = async (
    siteCode: string,
    fromDate: string,
    endDate: string
  ) => {
    try {
      setLoading(true);
      const data: RecentInsights[] = await GetAllRecentInsights(
        siteCode,
        fromDate,
        endDate
      );
      const formattedData: {
        groupName: string;
        deviceName: string;
        message: string;
        dateTime: string;
      }[] = data.map((item: InsightCardProps) => ({
        groupName: item.groupName,
        deviceName: item.deviceName,
        message: item.message,
        dateTime: item.dateTime,
      }));
      setInsights(formattedData);
    } catch (error) {
      console.error("Error fetching recent insights:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedSiteCode &&
      selectedSiteCode?.length > 0 &&
      fromDate &&
      endDate
    ) {
      fetchRecentInsights(selectedSiteCode, fromDate, endDate);
    }
  }, [selectedSiteCode, fromDate, endDate]);

  // Filter insights by date
  const filterByDate = (insights: InsightCardProps[]) => {
    const today: moment.Moment = moment();
    if (selectedDate === "today") {
      return insights.filter((insight) =>
        moment(insight.dateTime).isSame(today, "day")
      );
    } else if (selectedDate === "yesterday") {
      return insights.filter((insight) =>
        moment(insight.dateTime).isSame(moment().subtract(1, "day"), "day")
      );
    }

    return insights;
  };

  useEffect(() => {
    setPage(0);
  }, [selectedDate]);

  // Filter insights by search term and date
  const filteredInsights: InsightCardProps[] = filterByDate(
    insights.filter((insight) => {
      const groupName: string = insight.groupName?.toLowerCase() || "";
      const deviceName: string = insight.deviceName?.toLowerCase() || "";
      const message: string = insight.message?.toLowerCase() || "";
      const dateTime: string = moment(insight.dateTime)
        .format("ll")
        .toLowerCase();

      return (
        groupName.includes(searchTerm.toLowerCase()) ||
        deviceName.includes(searchTerm.toLowerCase()) ||
        message.includes(searchTerm.toLowerCase()) ||
        dateTime.includes(searchTerm.toLowerCase())
      );
    })
  );
  const paginatedInsights: InsightCardProps[] = filteredInsights.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when changing rows per page
  };

  const handleSearchChange = (newValue: string): void => {
    setSearchTerm(newValue);
    setPage(0); // Reset to the first page when filtering
  };

  const handleSiteChange = (event: SelectChangeEvent<string>): void => {
    const siteCode: string = event.target.value as string;
    setSelectedSiteCode(siteCode);
  };

  const InsightCard: React.FC<InsightCardProps> = ({
    groupName,
    deviceName,
    message,
    dateTime,
  }) => {
    return (
      <Card
        sx={{
          padding: 1.8,
          border: "1.5px solid #ccc",
          marginBottom: 1,
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: 1,
          }}
        >
          <Avatar sx={{ bgcolor: "orange", mr: 2 }}>
            {(groupName.split(" ")[0]?.[0] || "") +
              (groupName.split(" ")[1]?.[0] || "")}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{groupName}</span>
              <Typography noWrap variant="body2" color="textSecondary">
                {moment(dateTime).isSame(moment(), "day")
                  ? "Today"
                  : moment(dateTime).format("ll")}
              </Typography>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {deviceName}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1">{message}</Typography>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 10.5 }}>
      <Grid2 container>
        {/* Sidebar */}
        <Grid2
          size={{ xs: 12, sm: 4, md: 3.5, lg: 3, xl: 3 }}
          sx={{
            borderRight: "1px solid #ddd",
            padding: 1,
          }}
        >
          <FormLabel
            component="legend"
            sx={{ mb: 1, fontWeight: "bold", mt: 1 }}
          >
            {t("global.selectSite")}
          </FormLabel>
          <Select
            value={selectedSiteCode}
            onChange={handleSiteChange}
            fullWidth
            displayEmpty
            sx={{ mb: 2 }}
          >
            {siteOptions?.map((site) => (
              <MenuItem key={site.value} value={site.value}>
                {site.label}
              </MenuItem>
            ))}
          </Select>

          {/* Date selection */}
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: "bold" }}>
              {t("global.selectdate")}
            </FormLabel>
            <RadioGroup
              defaultValue="today"
              name="date-options"
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setFromDate(
                  e.target.value == "today"
                    ? moment().format("YYYY-MM-DD")
                    : e.target.value == "yesterday"
                    ? moment().subtract(1, "days").format("YYYY-MM-DD")
                    : moment().subtract(30, "days").format("YYYY-MM-DD")
                );
                setEndDate(
                  e.target.value == "today"
                    ? moment().format("YYYY-MM-DD")
                    : e.target.value == "yesterday"
                    ? moment().subtract(1, "days").format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD")
                );
              }}
            >
              <FormControlLabel
                value="today"
                control={<Radio />}
                label="Today"
              />
              <FormControlLabel
                value="yesterday"
                control={<Radio />}
                label="Yesterday"
              />
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="Last 30 Days"
              />
            </RadioGroup>
          </FormControl>
        </Grid2>

        {/* Insights Display */}
        <Grid2
          size={{ xs: 12, sm: 8, md: 8.5, lg: 9, xl: 9 }}
          sx={{
            paddingLeft: 2,
            paddingRight: 1,
            mt: 1,
          }}
        >
          {/* Search Input */}
          <Box sx={{ marginY: 2, display: "flex" }}>
            <SearchBar
              value={searchTerm}
              placeholder="Search Insights"
              style={{
                border: "1.5px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                fontSize: "14px",
                height: "50px",
                maxWidth: "600px",
              }}
              onChange={handleSearchChange}
            />
          </Box>
          {loading ? (
            <Loading loading={loading} type={`bars`} backDrop={false} />
          ) : (
            <Box
              className="scrollable-section"
              sx={{
                height: "calc(100vh - 200px)",
                overflow: "auto",
                paddingRight: "4px",
              }}
            >
              {paginatedInsights.map(
                (
                  insight: JSX.IntrinsicAttributes & InsightCardProps,
                  index: React.Key | null | undefined
                ) => (
                  <InsightCard key={index} {...insight} />
                )
              )}
            </Box>
          )}

          {/* Pagination */}
          <Box>
            <TablePagination
              component="div"
              count={filteredInsights.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 15]}
              labelRowsPerPage="Insights per page:"
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default InsightsScreen;
